import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '../config/api';
import TaskFormModal from './tasks/TaskFormModal';
import StatusBadge from './shared/StatusBadge';
import { usePagination } from '../hooks/usePagination';
import PaginationControls from './shared/PaginationControls';
import { useURLParams } from '../hooks/useURLParams';
import { formatDate } from '../utils/dateFormatter';
import { useColumnConfig } from '../hooks/useColumnConfig';
import { ColumnConfigDropdown } from './shared/ColumnConfigDropdown';

const statusColors = {
  todo: 'secondary',
  doing: 'primary',
  done: 'success'
};

const COLUMNS = [
  { key: 'priority', label: 'Priority' },
  { key: 'title', label: 'Title' },
  { key: 'assigned_to', label: 'Assigned To' },
  { key: 'due_date', label: 'Due Date' },
  { key: 'status', label: 'Status' },
  { key: 'related', label: 'Related' },
  { key: 'actions', label: 'Actions' },
];

export default function TaskList() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  // URL-synced filters
  const { getParam, setParam, setParams } = useURLParams({
    status: 'all', priority: 'all', search: '', page: 0,
  });
  const statusFilter = getParam('status');
  const priorityFilter = getParam('priority');
  const searchTerm = getParam('search');
  const urlPage = getParam('page');

  // Column config from URL
  const { visibleColumns, isVisible, toggleColumn, allColumns } = useColumnConfig(COLUMNS);

  const { apiCall } = useAuth();
  const navigate = useNavigate();

  const {
    pagination,
    setPagination,
    goToNextPage: _goToNextPage,
    goToPreviousPage: _goToPreviousPage,
    resetPagination,
    currentPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
  } = usePagination(20);

  // Set initial offset from URL
  useEffect(() => {
    if (urlPage > 0) {
      setPagination(prev => ({ ...prev, offset: urlPage * prev.limit }));
    }
  }, []); // Only on mount

  // Wrap pagination to sync URL
  const goToNextPage = useCallback(() => {
    _goToNextPage();
    const nextPage = Math.floor(pagination.offset / pagination.limit) + 1;
    setParam('page', nextPage, { replace: true });
  }, [_goToNextPage, pagination, setParam]);

  const goToPreviousPage = useCallback(() => {
    _goToPreviousPage();
    const prevPage = Math.floor(pagination.offset / pagination.limit) - 1;
    setParam('page', prevPage > 0 ? prevPage : '', { replace: true });
  }, [_goToPreviousPage, pagination, setParam]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (statusFilter !== 'all') queryParams.append('status', statusFilter);
      if (priorityFilter !== 'all') queryParams.append('priority', priorityFilter);
      queryParams.append('limit', pagination.limit);
      queryParams.append('offset', pagination.offset);

      const response = await apiCall(`${API_BASE_URL}/tasks?${queryParams.toString()}`);
      setTasks(response.data || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    resetPagination();
    setParam('page', '', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter]);

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, pagination.offset, pagination.limit]);

  const handleMarkDone = async (taskId) => {
    try {
      await apiCall(`${API_BASE_URL}/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'done' })
      });
      await loadTasks();
    } catch (error) {
      console.error('Failed to mark task as done:', error);
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setShowCreateModal(true);
  };

  const handleTaskSaved = () => {
    setShowCreateModal(false);
    setEditingTask(null);
    loadTasks();
  };

  const handleNavigateToEntity = (task) => {
    if (task.customer_id) {
      navigate(`/dashboard/customers/${task.customer_id}`);
    } else if (task.vehicle_id) {
      navigate(`/dashboard/vehicles/${task.vehicle_id}`);
    }
  };

  // Filter tasks by search term
  const filteredTasks = tasks.filter(task =>
    task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const renderCell = (key, task) => {
    switch (key) {
      case 'priority':
        return <StatusBadge status={task.priority} type="task" />;
      case 'title':
        return (
          <>
            <div className="fw-bold">{task.title}</div>
            {task.description && (
              <small className="text-muted d-block" style={{ maxWidth: '400px' }}>
                {task.description.length > 100
                  ? `${task.description.substring(0, 100)}...`
                  : task.description}
              </small>
            )}
          </>
        );
      case 'assigned_to':
        return task.assigned_agent_name ? (
          <>
            <i className="fas fa-user me-1 text-muted"></i>
            {task.assigned_agent_name}
          </>
        ) : (
          <span className="text-muted">Unassigned</span>
        );
      case 'due_date':
        return task.due_date ? (
          <>
            <i className="far fa-calendar me-1 text-muted"></i>
            {formatDate(task.due_date)}
          </>
        ) : (
          <span className="text-muted">&mdash;</span>
        );
      case 'status':
        return (
          <Badge bg={statusColors[task.status]}>
            {task.status === 'todo' ? 'TO DO' : task.status === 'doing' ? 'IN PROGRESS' : 'DONE'}
          </Badge>
        );
      case 'related':
        if (task.customer_id) {
          return (
            <Button variant="link" size="sm" className="p-0 d-block text-start" onClick={() => handleNavigateToEntity(task)}>
              <i className="fas fa-user me-1"></i>
              {task.first_name} {task.last_name}
            </Button>
          );
        }
        if (task.vehicle_id) {
          return (
            <Button variant="link" size="sm" className="p-0 d-block text-start" onClick={() => handleNavigateToEntity(task)}>
              <i className="fas fa-car me-1"></i>
              {task.year} {task.make} {task.model}
            </Button>
          );
        }
        return <span className="text-muted">&mdash;</span>;
      case 'actions':
        return (
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" onClick={() => handleEdit(task)} title="Edit task">
              <i className="fas fa-edit"></i>
            </Button>
            {task.status !== 'done' && (
              <Button variant="outline-success" size="sm" onClick={() => handleMarkDone(task.id)} title="Mark as done">
                <i className="fas fa-check"></i>
              </Button>
            )}
          </div>
        );
      default: return null;
    }
  };

  return (
    <Container fluid className="py-4 domain-tasks">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="fas fa-tasks me-2"></i>
            Task Management
          </h2>
          <p className="text-muted">Create, assign, and track tasks for your team</p>
        </Col>
        <Col className="text-end">
          <Button
            variant="primary"
            onClick={() => {
              setEditingTask(null);
              setShowCreateModal(true);
            }}
          >
            <i className="fas fa-plus me-2"></i>
            Create Task
          </Button>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={(e) => setParam('search', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setParam('status', e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="todo">To Do</option>
                  <option value="done">Done</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={priorityFilter}
                  onChange={(e) => setParam('priority', e.target.value)}
                >
                  <option value="all">All Priorities</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end justify-content-end">
              <ColumnConfigDropdown allColumns={allColumns} isVisible={isVisible} toggleColumn={toggleColumn} />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tasks Table */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading tasks...</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="text-center text-muted p-5">
              <i className="fas fa-tasks fa-3x mb-3 d-block"></i>
              <h5>No tasks found</h5>
              <p>
                {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first task to get started'}
              </p>
            </div>
          ) : (
            <Table striped hover responsive className="mb-0">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col.key} className={col.className}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map(task => (
                  <tr key={task.id}>
                    {visibleColumns.map(col => (
                      <td key={col.key} className={col.className}>{renderCell(col.key, task)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <PaginationControls
            pagination={pagination}
            onNextPage={goToNextPage}
            onPreviousPage={goToPreviousPage}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            currentPage={currentPage}
            totalPages={totalPages}
          />
        </Card.Body>
      </Card>

      <TaskFormModal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setEditingTask(null);
        }}
        onTaskCreated={handleTaskSaved}
        initialData={editingTask}
      />
    </Container>
  );
}
